import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";

import "@/assets/fonts/fonts.css";
import { ErrorBoundary, Snacks } from "@/components";
import "@/i18n/config";
import "@/index.css";
import {
  AppAuthProvider,
  AppCustomerProvider,
  AppQueryClientProvider,
  AppThemeProvider,
} from "@/providers";

import App from "./App";
import { Confetti } from "./components/confetti/Confetti";

/**
 * Global error handler for chunk loading failures
 * This happens when users have an old version open and try to load chunks
 * from a new deployment that no longer exist
 */
function setupChunkLoadErrorHandler() {
  window.addEventListener("error", (event) => {
    const { message } = event;

    const isChunkLoadError =
      message?.includes("Failed to fetch dynamically imported module") ||
      message?.includes("Importing a module script failed") ||
      message?.includes("Failed to load module script");

    if (isChunkLoadError) {
      console.warn(
        "[App] Detected chunk load error - likely due to deployment. Reloading page...",
      );
      event.preventDefault();
      window.location.reload();
    }
  });

  window.addEventListener("unhandledrejection", (event) => {
    const { reason } = event;

    const isChunkLoadError =
      reason?.message?.includes(
        "Failed to fetch dynamically imported module",
      ) ||
      reason?.message?.includes("Importing a module script failed") ||
      reason?.name === "ChunkLoadError";

    if (isChunkLoadError) {
      console.warn(
        "[App] Detected chunk load error in promise - likely due to deployment. Reloading page...",
      );
      event.preventDefault();
      window.location.reload();
    }
  });
}

function initializeApp() {
  setupChunkLoadErrorHandler();

  const rootElement = document.getElementById("root");
  if (!rootElement) {
    console.error("[App] Root element not found!");
    return;
  }

  try {
    ReactDOM.createRoot(rootElement).render(
      <React.StrictMode>
        <BrowserRouter>
          <AppThemeProvider>
            <AppQueryClientProvider>
              <AppAuthProvider>
                <AppCustomerProvider>
                  <ErrorBoundary>
                    <App />
                  </ErrorBoundary>
                </AppCustomerProvider>
              </AppAuthProvider>
            </AppQueryClientProvider>
            <Confetti />
            <Snacks />
          </AppThemeProvider>
        </BrowserRouter>
      </React.StrictMode>,
    );
  } catch (error) {
    console.error("[App] Error rendering React app:", error);
    rootElement.innerHTML = `
      <div style="padding: 20px; color: red; font-family: sans-serif;">
        <h1>Failed to load app</h1>
        <pre>${error}</pre>
      </div>
    `;
  }
}

initializeApp();
